/**
 * EventTrackingPage
 *
 * Tab landing for "Hub Meetings & Trainings". Toggles between list and
 * create flows. Mirrors the OutreachActivitiesPage pattern from Phase 4a.
 */

import React, { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EventsListPage } from './EventsListPage';
import { CreateEventForm } from './CreateEventForm';
import { ACTIVITY_EVENT_DEEPLINK_KEY } from '../calendar/Calendar';

type Mode = 'list' | 'create';

interface EventTrackingPageProps {
  /** App-shell tab switcher — forwarded to EventsListPage so the
   *  "View on calendar" button can jump to the main Calendar tab. */
  onNavigate?: (tab: string) => void;
}

export const EventTrackingPage: React.FC<EventTrackingPageProps> = ({ onNavigate }) => {
  const [mode, setMode] = useState<Mode>('list');
  const [reloadKey, setReloadKey] = useState(0);

  // Cross-tab deep-link: Calendar.tsx writes the event id into sessionStorage
  // before navigating here. We read it once and forward to EventsListPage
  // (which short-circuits to the full-screen EventDetailPage).
  const [initialEventId, setInitialEventId] = useState<number | null>(() => {
    try {
      const raw = sessionStorage.getItem(ACTIVITY_EVENT_DEEPLINK_KEY);
      if (!raw) return null;
      sessionStorage.removeItem(ACTIVITY_EVENT_DEEPLINK_KEY);
      const id = Number(raw);
      return Number.isFinite(id) ? id : null;
    } catch {
      return null;
    }
  });

  // One-shot — after the first render hands it to EventsListPage as the
  // initial open event, clear it so subsequent re-renders don't re-open.
  useEffect(() => {
    if (initialEventId != null) {
      // Small delay so the child commits first; otherwise the next render
      // would still pass the stale value.
      const t = setTimeout(() => setInitialEventId(null), 0);
      return () => clearTimeout(t);
    }
  }, [initialEventId]);

  if (mode === 'create') {
    return (
      <div className="p-4">
        <div className="max-w-4xl mx-auto mb-2">
          <Button variant="ghost" size="sm" onClick={() => setMode('list')}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to events
          </Button>
        </div>
        <CreateEventForm
          onCancel={() => setMode('list')}
          onCreated={() => {
            setReloadKey((k) => k + 1);
            setTimeout(() => setMode('list'), 1500);
          }}
        />
      </div>
    );
  }

  return (
    <EventsListPage
      key={reloadKey}
      onCreateClick={() => setMode('create')}
      onNavigate={onNavigate}
      initialEventId={initialEventId}
    />
  );
};

export default EventTrackingPage;
